export type ChapterId = 'discover' | 'evaluate' | 'forecast' | 'track';

export interface ChapterMeta {
  id: ChapterId;
  title: string;
  /** Start time in ms within the loop */
  startMs: number;
  /** End time in ms within the loop (exclusive of soft hold into next) */
  endMs: number;
}

/** Total loop duration in milliseconds (~18s with Track paused). */
export const LOOP_DURATION_MS = 15_000;
// Was 16_000 with Track included; lengthened so in-scene beats read more clearly.

export const CHAPTERS: ChapterMeta[] = [
  { id: 'discover', title: 'Discover', startMs: 0, endMs: 4_000 },
  { id: 'evaluate', title: 'Evaluate', startMs: 4_000, endMs: 10_000 },
  { id: 'forecast', title: 'Forecast', startMs: 10_000, endMs: 15_000 },
  // TODO: re-enable Track chapter when scene is ready
  // { id: 'track', title: 'Track', startMs: 18_000, endMs: 24_000 },
];

export const DEMO_COMPANY = {
  symbol: 'NVRA',
  name: 'Novaura',
  sector: 'Software',
  exchange: 'NASDAQ',
  /** Soft brand tint for monogram logo */
  logoTone: '#34d399',
};

/** Sample ticker for the Evaluate ticker-breakdown scene. */
export const EVALUATE_COMPANY = {
  symbol: 'NVDA',
  name: 'NVIDIA Corp',
  exchange: 'NASDAQ',
  logoTone: '#76b900',
};

export const DISCOVER_CRITERIA = [
  { id: 'rev', label: 'Revenue Growth', shortLabel: 'Rev Growth', threshold: '> 15%' },
  { id: 'gm', label: 'Gross Margin', shortLabel: 'Gross Margin', threshold: '> 40%' },
  { id: 'roe', label: 'ROE', shortLabel: 'ROE', threshold: '> 15%' },
] as const;

/**
 * Stylized ticker list for Discover.
 * surviveLevel: 0 culled by first lens, 1 by second, 2 by third, 3 remains.
 */
export interface DiscoverTicker {
  id: string;
  symbol: string;
  name: string;
  sector: string;
  logoTone: string;
  surviveLevel: 0 | 1 | 2 | 3;
}

export const DISCOVER_TICKERS: DiscoverTicker[] = [
  { id: 't0', symbol: 'NVRA', name: 'Novaura', sector: 'Software', logoTone: '#34d399', surviveLevel: 3 },
  { id: 't1', symbol: 'AXYN', name: 'Axyon Labs', sector: 'Technology', logoTone: '#60a5fa', surviveLevel: 3 },
  { id: 't2', symbol: 'KELD', name: 'Keldran', sector: 'Industrials', logoTone: '#a78bfa', surviveLevel: 3 },
  { id: 't3', symbol: 'ORIS', name: 'Orison Health', sector: 'Healthcare', logoTone: '#2dd4bf', surviveLevel: 3 },
  { id: 't4', symbol: 'VELA', name: 'Velara', sector: 'Consumer', logoTone: '#fbbf24', surviveLevel: 3 },
  { id: 't5', symbol: 'TREM', name: 'Tremont', sector: 'Energy', logoTone: '#fb7185', surviveLevel: 3 },
  { id: 't6', symbol: 'LYRA', name: 'Lyra Systems', sector: 'Software', logoTone: '#818cf8', surviveLevel: 3 },
  { id: 't7', symbol: 'QUEN', name: 'Quentra', sector: 'Finance', logoTone: '#94a3b8', surviveLevel: 2 },
  { id: 't8', symbol: 'BRIX', name: 'Brixwell', sector: 'Materials', logoTone: '#94a3b8', surviveLevel: 2 },
  { id: 't9', symbol: 'HALO', name: 'HaloNet', sector: 'Telecom', logoTone: '#94a3b8', surviveLevel: 2 },
  { id: 't10', symbol: 'NEXA', name: 'Nexa Foods', sector: 'Consumer', logoTone: '#94a3b8', surviveLevel: 1 },
  { id: 't11', symbol: 'SOLR', name: 'Solara', sector: 'Utilities', logoTone: '#94a3b8', surviveLevel: 1 },
  { id: 't12', symbol: 'PINE', name: 'Pinecrest', sector: 'REITs', logoTone: '#94a3b8', surviveLevel: 1 },
  { id: 't13', symbol: 'DRFT', name: 'Driftline', sector: 'Transport', logoTone: '#64748b', surviveLevel: 0 },
  { id: 't14', symbol: 'MOOR', name: 'Moorfield', sector: 'Finance', logoTone: '#64748b', surviveLevel: 0 },
  { id: 't15', symbol: 'CIND', name: 'Cinder Co', sector: 'Materials', logoTone: '#64748b', surviveLevel: 0 },
  { id: 't16', symbol: 'WISP', name: 'Wisper', sector: 'Media', logoTone: '#64748b', surviveLevel: 0 },
  { id: 't17', symbol: 'FATH', name: 'Fathom AI', sector: 'Technology', logoTone: '#64748b', surviveLevel: 0 },
  { id: 't18', symbol: 'GROV', name: 'Grove Bio', sector: 'Healthcare', logoTone: '#64748b', surviveLevel: 0 },
  { id: 't19', symbol: 'RILL', name: 'Rillwater', sector: 'Utilities', logoTone: '#64748b', surviveLevel: 0 },
];

/** Ticker-breakdown rail items, column-major like the product (3 columns). */
export const EVALUATE_NAV = [
  { id: 'key-stats', label: 'Key Stats', shortLabel: 'Key Stats' },
  { id: 'financials', label: 'Financials', shortLabel: 'Financials' },
  { id: 'snapshots', label: 'Snapshots', shortLabel: 'Snapshots' },
  { id: 'transcripts', label: 'Earnings Transcripts', shortLabel: 'Transcripts' },
  { id: 'earn-trend', label: 'Earnings Trend', shortLabel: 'Earn. Trend' },
  { id: 'filings', label: 'Filings', shortLabel: 'Filings' },
  { id: 'insiders', label: 'Insider Trades', shortLabel: 'Insiders' },
  { id: 'analysts', label: 'Analysts', shortLabel: 'Analysts' },
  { id: 'institutions', label: 'Institutions', shortLabel: 'Institutions' },
  { id: 'leadership', label: 'Leadership', shortLabel: 'Leadership' },
  { id: 'competitors', label: 'Competitors', shortLabel: 'Competitors' },
  { id: 'shares', label: 'Shares Stats', shortLabel: 'Shares Stats' },
] as const;

export const EVALUATE_PRICE = {
  last: '$208.92',
  change: '$3.53 (−1.66%)',
  axis: ['$211', '$209'] as const,
};

/** Sparkline sample (higher = higher price). Drawn left → right. */
export const EVALUATE_SPARKLINE = [
  36, 48, 42, 70, 52, 40, 58, 74, 88, 60, 48, 64, 50, 28, 22, 46, 54, 38, 44, 34,
];

export const EVALUATE_CHART_COLORS = {
  revenue: '#008FFB',
  cogs: '#00E396',
} as const;

export type EvaluateDensity = 'always' | 'md' | 'lg';

export const EVALUATE_FINANCIAL_PERIODS = [
  {
    id: 'y22',
    year: '2022',
    revenue: 4.2,
    cogs: 2.1,
    revLabel: '4.2B',
    cogsLabel: '2.1B',
    density: 'lg' as const,
  },
  {
    id: 'y23',
    year: '2023',
    revenue: 6.8,
    cogs: 3.2,
    revLabel: '6.8B',
    cogsLabel: '3.2B',
    density: 'md' as const,
  },
  {
    id: 'y24',
    year: '2024',
    revenue: 12.4,
    cogs: 5.1,
    revLabel: '12.4B',
    cogsLabel: '5.1B',
    density: 'always' as const,
  },
  {
    id: 'y25',
    year: '2025',
    revenue: 21.8,
    cogs: 8.4,
    revLabel: '21.8B',
    cogsLabel: '8.4B',
    density: 'always' as const,
  },
  {
    id: 'ttm',
    year: 'TTM',
    revenue: 28.6,
    cogs: 10.2,
    revLabel: '28.6B',
    cogsLabel: '10.2B',
    density: 'always' as const,
  },
] as const;

export const EVALUATE_STATEMENT_ROWS = [
  {
    id: 'rev',
    label: 'Revenue',
    tone: 'revenue' as const,
    values: ['4.2B', '6.8B', '12.4B', '21.8B', '28.6B'],
    yoy: ['+38%', '+62%', '+82%', '+76%', '+31%'],
  },
  {
    id: 'cogs',
    label: 'Cost of Revenue',
    tone: 'cogs' as const,
    values: ['2.1B', '3.2B', '5.1B', '8.4B', '10.2B'],
    yoy: ['+22%', '+52%', '+59%', '+65%', '+21%'],
  },
  {
    id: 'gp',
    label: 'Gross Profit',
    tone: 'plain' as const,
    values: ['2.1B', '3.6B', '7.3B', '13.4B', '18.4B'],
    yoy: ['+48%', '+71%', '+103%', '+84%', '+37%'],
  },
  {
    id: 'rd',
    label: 'Research & Development',
    shortLabel: 'R&D',
    tone: 'plain' as const,
    values: ['0.9B', '1.4B', '2.4B', '4.1B', '5.2B'],
    yoy: ['+28%', '+56%', '+71%', '+71%', '+27%'],
  },
  {
    id: 'ni',
    label: 'Net Income',
    tone: 'plain' as const,
    values: ['0.5B', '1.1B', '2.8B', '5.9B', '8.6B'],
    yoy: ['+62%', '+120%', '+155%', '+111%', '+46%'],
  },
] as const;

export const FORECAST_COLORS = {
  bull: '#16a34a',
  base: '#2563eb',
  bear: '#ea580c',
  created: '#000000',
  now: '#ca8a04',
} as const;

export const FORECAST_CHART = {
  createdPrice: 480,
  createdTag: '$480',
  createdCaption: 'Mar 2026',
  nowPrice: 516,
  nowTag: '$516',
  endYear: 2031,
  years: 5,
} as const;

/** t = 0 at "now", t = 1 at projection end. Bear dips before recovering. */
export const FORECAST_PATHS = {
  /** ~6mo history from created → now — longer black stem before the fan. */
  historical: [
    { t: 0, price: 480 },
    { t: 0.22, price: 468 },
    { t: 0.42, price: 492 },
    { t: 0.62, price: 505 },
    { t: 0.82, price: 498 },
    { t: 1, price: 516 },
  ],
  bull: [
    { t: 0, price: 516 },
    { t: 0.18, price: 690 },
    { t: 0.36, price: 920 },
    { t: 0.54, price: 1220 },
    { t: 0.72, price: 1520 },
    { t: 0.88, price: 1780 },
    { t: 1, price: 1958 },
  ],
  base: [
    { t: 0, price: 516 },
    { t: 0.18, price: 620 },
    { t: 0.36, price: 780 },
    { t: 0.54, price: 980 },
    { t: 0.72, price: 1180 },
    { t: 0.88, price: 1320 },
    { t: 1, price: 1411 },
  ],
  bear: [
    { t: 0, price: 516 },
    { t: 0.18, price: 460 },
    { t: 0.36, price: 430 },
    { t: 0.54, price: 470 },
    { t: 0.72, price: 540 },
    { t: 0.88, price: 600 },
    { t: 1, price: 637 },
  ],
} as const;

export const FORECAST_SCENARIOS = {
  bull: {
    key: 'bull' as const,
    label: 'Bull',
    color: FORECAST_COLORS.bull,
    textClass: 'text-emerald-600',
    bgClass: 'bg-gradient-to-br from-emerald-50/90 to-emerald-50/40',
    borderClass: 'border-emerald-300',
    price: 1958,
    boostedPrice: 2251,
    growth: 280,
    boostedGrowth: 336,
    revenueB: 489.87,
    boostedRevenueB: 563.5,
    marketCapT: 7.35,
    boostedMarketCapT: 8.45,
    cagr: 38,
    boostedCagr: 42,
    multiple: 15,
  },
  base: {
    key: 'base' as const,
    label: 'Base',
    color: FORECAST_COLORS.base,
    textClass: 'text-blue-600',
    bgClass: 'bg-gradient-to-br from-blue-50/90 to-blue-50/40',
    borderClass: 'border-blue-300',
    price: 1411,
    boostedPrice: 1573,
    growth: 173,
    boostedGrowth: 205,
    revenueB: 407.33,
    boostedRevenueB: 454.2,
    marketCapT: 5.3,
    boostedMarketCapT: 5.91,
    cagr: 33,
    boostedCagr: 36,
    multiple: 13,
  },
  bear: {
    key: 'bear' as const,
    label: 'Bear',
    color: FORECAST_COLORS.bear,
    textClass: 'text-orange-600',
    bgClass: 'bg-gradient-to-br from-orange-50/90 to-orange-50/40',
    borderClass: 'border-orange-300',
    price: 637,
    boostedPrice: 687,
    growth: 23,
    boostedGrowth: 33,
    revenueB: 298.7,
    boostedRevenueB: 322.4,
    marketCapT: 2.39,
    boostedMarketCapT: 2.58,
    cagr: 25,
    boostedCagr: 27,
    multiple: 8,
  },
} as const;

export const FORECAST_SCENARIO_ORDER = ['bull', 'base', 'bear'] as const;

/** Weekly research digest cards for the Track chapter. */
export const TRACK_DIGEST = {
  weekLabel: 'Week of Sep 22',
  signalCount: 7,
  holdingCount: 3,
  holdings: [
    {
      id: 'nvda',
      symbol: 'NVDA',
      name: 'NVIDIA',
      logoTone: '#76b900',
      changePct: '+4.2%',
      positive: true,
      tag: 'Bullish',
      tagClass: 'bg-emerald-100 text-emerald-800',
      signal: 'Earnings beat · AI guidance raised',
      strength: 0.92,
    },
    {
      id: 'aapl',
      symbol: 'AAPL',
      name: 'Apple',
      logoTone: '#64748b',
      changePct: '+1.1%',
      positive: true,
      tag: 'Watch',
      tagClass: 'bg-amber-100 text-amber-800',
      signal: 'Services record · India capacity up',
      strength: 0.64,
    },
    {
      id: 'msft',
      symbol: 'MSFT',
      name: 'Microsoft',
      logoTone: '#3b82f6',
      changePct: '−0.8%',
      positive: false,
      tag: 'Neutral',
      tagClass: 'bg-slate-100 text-slate-700',
      signal: 'Azure cools · Capex still rising',
      strength: 0.48,
    },
  ],
} as const;

/** Count milestones during discover culling */
export const DISCOVER_COUNTS = [2847, 1264, 682, 318] as const;

export function chapterIndexFromTime(tMs: number): number {
  const t = ((tMs % LOOP_DURATION_MS) + LOOP_DURATION_MS) % LOOP_DURATION_MS;
  for (let i = CHAPTERS.length - 1; i >= 0; i--) {
    if (t >= CHAPTERS[i].startMs) return i;
  }
  return 0;
}

export function chapterProgress(tMs: number, chapterIndex: number): number {
  const chapter = CHAPTERS[chapterIndex];
  const t = ((tMs % LOOP_DURATION_MS) + LOOP_DURATION_MS) % LOOP_DURATION_MS;
  const local = t - chapter.startMs;
  const duration = chapter.endMs - chapter.startMs;
  return Math.min(1, Math.max(0, local / duration));
}
